/**
 * `/onboarding`: the guided first run as one conversation with the
 * onboarding agent, in the app shell with only its Get Started tab (sidebar
 * links ask before skipping setup). The thread is created (with
 * its kickoff turn) by `POST /api/onboarding/start`, so the agent speaks
 * first. Questions dock above the composer, cards render in the transcript,
 * and the Get Started checklist floats bottom right, derived from what is
 * actually saved.
 */
import { useCallback, useEffect, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { BrandLoader } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { AgentComposerBar } from "@/features/agents/components/AgentComposerBar";
import { AgentTranscript } from "@/features/agents/components/AgentTranscript";
import type { AgentScope } from "@/features/agents/types";
import { useAgentSpecs } from "@/features/agents/useAgentSpecs";
import { useScopedAgentChat } from "@/features/agents/useScopedAgentChat";
import { HOME_PATH } from "@/features/dashboard/routes";

import { OnboardingAPI } from "../api/onboardingApi";
import { FinishDock } from "../components/FinishDock";
import {
  GetStartedChecklist,
  isComplete
} from "../components/GetStartedChecklist";
import { OnboardingHeader } from "../components/OnboardingHeader";
import { firmItemCopy, openFirmItems, STEP_PROMPTS } from "../firmItems";
import { applyOnboarding, useOnboarding } from "../hooks/useOnboarding";
import { ONBOARDING_OWNER, ONBOARDING_SPEC } from "../spec";

const START_RETRIES = 4;
const START_RETRY_MS = 1500;
/** How long an empty thread waits for the kickoff turn before offering to start. */
const KICKOFF_GRACE_MS = 5000;

const useElapsed = (ms: number): boolean => {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setElapsed(true), ms);
    return () => clearTimeout(t);
  }, [ms]);
  return elapsed;
};

/** Start (or reopen) the member's onboarding thread; retries while another tab starts it. */
const useOnboardingThread = () => {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const run = async (tries: number) => {
      try {
        const progress = await OnboardingAPI.start();
        if (cancelled) return;
        applyOnboarding(progress);
        if (progress.thread_id) setThreadId(progress.thread_id);
        else setError("Onboarding could not start.");
      } catch {
        if (cancelled) return;
        if (tries > 1)
          timer = setTimeout(() => void run(tries - 1), START_RETRY_MS);
        else setError("Onboarding could not start. Try again in a moment.");
      }
    };
    setError(null);
    void run(START_RETRIES);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [attempt]);
  return { threadId, error, retry: () => setAttempt((n) => n + 1) };
};

const Conversation: FC<{ scope: AgentScope; threadId: string }> = ({
  scope,
  threadId
}) => {
  const navigate = useNavigate();
  const { progress } = useOnboarding();
  const scoped = useScopedAgentChat({
    agent: "practice",
    agentKind: "onboarding",
    scope,
    spec: ONBOARDING_SPEC,
    owner: ONBOARDING_OWNER,
    fixedThreadId: threadId
  });
  const { chat, send, empty, streaming } = scoped;
  const goHome = useCallback(() => navigate(HOME_PATH), [navigate]);
  const complete = progress ? isComplete(progress) : false;
  const firmOpen = progress
    ? openFirmItems(progress.firm_items).filter((i) => i.can_edit)
    : [];
  const graceOver = useElapsed(KICKOFF_GRACE_MS);
  const kickoffPending =
    empty && !streaming && (Boolean(chat.activeTurn) || !graceOver);
  const stalled = empty && !streaming && !kickoffPending;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {kickoffPending ? (
        <div
          className="flex min-h-0 flex-1 items-center justify-center"
          data-testid="onboarding-waiting"
        >
          <BrandLoader size="md" label="Getting things ready" />
        </div>
      ) : stalled ? (
        <div className="mx-auto flex min-h-0 w-full max-w-150 flex-1 flex-col justify-end gap-3 pb-4">
          <h1 className="text-xl font-medium text-fg">Let's Get You Set Up</h1>
          <p className="text-md text-fg-2">
            A few minutes: your tools, your profile, your agents and how I reach
            you. You can stop anytime and pick up from the checklist.
          </p>
          <Button
            variant="primary"
            size="md"
            className="self-start"
            onClick={() => send(STEP_PROMPTS.welcome)}
            data-testid="onboarding-start"
          >
            Get Started
          </Button>
        </div>
      ) : (
        <AgentTranscript
          scoped={scoped}
          contentClassName="mx-auto w-full max-w-150 px-0 pt-14 pb-4"
        />
      )}
      <AgentComposerBar
        scoped={scoped}
        className="mx-auto w-full max-w-150 pb-5"
        placeholder="Reply, or ask me anything"
        hideDisclaimer
        autoFocus
        research={false}
        above={
          complete ? (
            <FinishDock
              firmOpen={firmOpen.length}
              onKeepGoing={() => send("Let's keep going with the firm setup.")}
              onGoHome={goHome}
            />
          ) : null
        }
      />
      {progress ? (
        <GetStartedChecklist
          progress={progress}
          className="absolute right-4 bottom-4 z-10"
          defaultCollapsed={!complete}
          onStep={(step) => send(STEP_PROMPTS[step.id])}
          onFirmItem={(item) => send(firmItemCopy(item.id).prompt)}
        />
      ) : null}
    </div>
  );
};

export const OnboardingPage: FC = () => {
  const navigate = useNavigate();
  const { practiceScope, error: specsError } = useAgentSpecs();
  const { threadId, error, retry } = useOnboardingThread();
  const failed = error ?? (specsError ? "Onboarding could not load." : null);
  const finishLater = useCallback(() => navigate(HOME_PATH), [navigate]);

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-surface"
      data-testid="onboarding-page"
    >
      <OnboardingHeader onFinishLater={finishLater} />
      {failed ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 text-base text-fg-3">
          <span>{failed}</span>
          <Button variant="secondary" size="sm" onClick={retry}>
            Try Again
          </Button>
        </div>
      ) : practiceScope && threadId ? (
        <Conversation scope={practiceScope} threadId={threadId} />
      ) : (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <BrandLoader size="md" label="Getting things ready" />
        </div>
      )}
    </div>
  );
};
